import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collectionVis, copiedCollectionMessage, copyText, studyThumbnail, useCollectionMutations, useCollections, useWritableTargets } from '../api/collections';
import { routeBase } from '../api/collections';
import { BrowseBar, type BrowseShare, type BrowseState, type BrowseTab, type BrowseView } from '../ui/BrowseBar';
import { Button } from '../ui/Button';
import { MosaicCard, MosaicMini } from '../ui/Cards';
import { confirm } from '../ui/Confirm';
import { Checkbox, Pager } from '../ui/Controls';
import { IconHeart, IconPlus } from '../ui/icons';
import { plainText } from '../ui/RichText';
import { SelectionToolbar } from '../ui/SelectionToolbar';
import { toast } from '../ui/toast';
import { VIS_LABEL, VisIcon, toServerVisibility } from '../ui/Visibility';
import { useSelectedWorkspace } from '../../stores/workspaceStore';

/**
 * Collections (workspace). Browse Bar, 12 Mosaic Cards per page or the list
 * rows, Pager. Every browse control lives in the URL so a page is a link.
 *
 * Cards and rows tick, and the selection toolbar at the bottom is the same one
 * the collection page uses — minus Move, which a collection has nowhere to go
 * to: a collection does not leave its workspace, it is COPIED into another the
 * person administers. Delete here destroys the collections AND their studies,
 * which is what the server does and what the confirm says out loud.
 */
const PAGE_SIZE = 12;

export function useBrowseParams(defaults?: Partial<BrowseState>) {
  const [params, setParams] = useSearchParams();
  const state: BrowseState = {
    tab: (params.get('tab') as BrowseTab) || defaults?.tab || 'all',
    view: (params.get('view') as BrowseView) || defaults?.view || 'grid',
    share: (params.get('vis') as BrowseShare) || defaults?.share || 'all',
    query: params.get('q') || '',
  };
  const page = Math.max(1, Number(params.get('page') || 1) || 1);
  const set = (next: BrowseState, nextPage = 1) => {
    const p = new URLSearchParams(params);
    const put = (k: string, v: string, dflt: string) => (v && v !== dflt ? p.set(k, v) : p.delete(k));
    put('tab', next.tab, 'all');
    put('view', next.view, 'grid');
    put('vis', next.share, 'all');
    put('q', next.query, '');
    put('page', String(nextPage), '1');
    setParams(p, { replace: true });
  };
  return { state, page, set, setPage: (n: number) => set(state, n) };
}

export function CollectionsPage() {
  const nav = useNavigate();
  const { state, page, set, setPage } = useBrowseParams();
  const q = useMemo(
    () => ({ q: state.query || undefined, tab: state.tab, visibility: state.share === 'all' ? 'all' : toServerVisibility(state.share), page, limit: PAGE_SIZE }) as const,
    [state.query, state.tab, state.share, page],
  );
  const { data } = useCollections(q);
  const m = useCollectionMutations();
  /**
   * Whether this person may CHANGE this workspace: an admin or editor, per the
   * server (GET /collections). Viewers browse and save, and are offered nothing
   * that writes.
   *
   * Undefined until the first page lands, and `!== true` is the test used
   * everywhere below so that gap reads as "no" — the controls appear when
   * permission is known, never while it is merely unrefuted.
   *
   * The page used to offer a viewer "New collection" and a bulk Delete, both of
   * which the server refuses. The cost was not the refusal: it was confirming
   * "and its 47 studies. This cannot be undone" and then being told "not found"
   * about a collection plainly on screen.
   */
  const editable = data?.editable === true;
  // Where a whole collection can be copied: the workspaces this person
  // administers, this one included, and only when they administer this one too.
  const targets = useWritableTargets();
  const { current: workspace } = useSelectedWorkspace();
  const writable = targets.data?.workspaces ?? [];
  const copyWorkspaces = writable.some((w) => w.public_id === workspace?.public_id && w.is_admin)
    ? writable.filter((w) => w.public_id !== workspace?.public_id && w.is_admin).map((w) => ({ public_id: w.public_id, name: w.name }))
    : [];
  const rows = data?.collections ?? [];
  const total = data?.total ?? 0;

  // ── selection ──
  // Keyed by public id, so it survives a re-order or a refetch. Select all
  // means the page in front of the person, not every page of the workspace.
  const [selected, setSelected] = useState<Record<string, true>>({});
  const selIds = Object.keys(selected);
  const selRows = rows.filter((c) => selected[c.public_id]);
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = { ...s };
      if (n[id]) delete n[id];
      else n[id] = true;
      return n;
    });
  const clearSelection = () => setSelected({});
  // And it is only ever what is on screen. A filter or a page change leaves
  // rows the person can no longer see; Delete still took them, behind a
  // confirm that could not count their studies and so did not mention them.
  // A WORKSPACE switch is the same thing, and the worst case of it: the ids
  // ticked in one workspace survived into the next, where Delete destroyed
  // collections the person was no longer even looking at.
  useEffect(() => setSelected({}), [q, workspace?.public_id]);
  const selectAll = () => setSelected(Object.fromEntries(rows.map((c) => [c.public_id, true as const])));
  const allSelected = rows.length > 0 && rows.every((c) => selected[c.public_id]);
  const copySelected = async (to: { public_id: string; name: string }) => {
    try {
      const made = [];
      for (const id of selIds) made.push(await m.copyCollection.mutateAsync({ id, workspace: to.public_id }));
      clearSelection();
      toast(made.length === 1
        ? copiedCollectionMessage(made[0], to.name)
        : `Copied ${made.length} collections to ${to.name}${made.some((r) => r.copying > 0) ? '. The images are being copied and appear when done' : ''}`);
    } catch {
      /* toasted by the mutation */
    }
  };
  const deleteSelected = async () => {
    const n = selIds.length;
    // Only the rows on this page can be counted; a selection carried over from
    // another page is still deleted, so the studies line stays deliberately
    // vague rather than naming a number that is short.
    const studies = selRows.reduce((a, c) => a + c.studies, 0);
    const ok = await confirm({
      title: `Delete ${n} ${n === 1 ? 'collection' : 'collections'}?`,
      body: studies
        ? `The ${studies} ${studies === 1 ? 'study' : 'studies'} inside ${n === 1 ? 'it' : 'them'} ${studies === 1 ? 'is' : 'are'} deleted too. This cannot be undone.`
        : 'This cannot be undone.',
      action: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await m.removeMany.mutateAsync(selIds);
      // Only what is left to try stays ticked: pressing Delete again must not
      // start on an id that is already gone.
      setSelected(Object.fromEntries(r.failed.map((f) => [f.id, true as const])));
      toast(
        r.failed.length
          ? `${r.deleted} deleted · ${r.failed.length} could not be (${r.failed[0].message})`
          : `${r.deleted} ${r.deleted === 1 ? 'collection' : 'collections'} deleted`,
      );
    } catch {
      /* toasted by the mutation */
    }
  };

  const newCollection = async () => {
    // The prototype creates "New collection" and drops into the title. Names
    // are unique per workspace, so a second one gets a number.
    for (let n = 1; n <= 20; n++) {
      const name = n === 1 ? 'New collection' : `New collection ${n}`;
      try {
        const c = await m.create.mutateAsync(name);
        nav(`${routeBase()}/collections/${c.public_id}?edit=title&new=1`);
        return;
      } catch (e) {
        if (!(e instanceof Error) || !/already exists/.test(e.message)) return;
      }
    }
  };

  const noMatch = total === 0 && (state.query || state.tab === 'saved' || state.share !== 'all');
  const noMatchText = state.query.trim()
    ? `No collections match “${state.query.trim()}”`
    : state.tab === 'saved'
      ? 'No saved collections yet'
      : `No ${VIS_LABEL[state.share as 'private' | 'link' | 'public']?.toLowerCase() ?? ''} collections`;

  return (
    <div className="tr-page" style={{ padding: '36px 28px 44px', display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1440, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-.02em' }}>Collections</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {editable ? (
            <Button variant="outlined" size={32} icon={<IconPlus size={14} />} onClick={newCollection}>
              New collection
            </Button>
          ) : null}
        </div>
      </div>
      <BrowseBar allCount={data ? (state.tab === 'saved' ? total : total) : 0} savedCount={data?.saved ?? 0} state={state} placeholder="Filter collections" onChange={(s) => set(s)} />
      {noMatch ? <div style={{ padding: '22px 16px', border: '1px solid #ebebea', borderRadius: 10, fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>{noMatchText}</div> : null}
      {state.view === 'grid' ? (
        <div className="tr-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px 20px' }}>
          {rows.map((c) => (
            <MosaicCard
              key={c.public_id}
              c1={c.covers[0] ? studyThumbnail(c.covers[0]) : undefined}
              c2={c.covers[1] ? studyThumbnail(c.covers[1]) : undefined}
              c3={c.covers[2] ? studyThumbnail(c.covers[2]) : undefined}
              count={c.studies}
              vis={collectionVis(c)}
              title={c.name}
              desc={plainText(c.description)}
              meta={c.author ?? ''}
              likes={c.like_count}
              liked={c.liked}
              discussion={c.comment_count}
              selected={!!selected[c.public_id]}
              // No checkbox for a viewer: the only things a selection leads to
              // here are Copy and Delete, and neither is theirs.
              onToggle={editable ? () => toggle(c.public_id) : undefined}
              onOpen={() => nav(`${routeBase()}/collections/${c.public_id}`)}
              onToggleLike={() => m.like.mutate({ kind: 'collection', id: c.public_id, liked: c.liked })}
            />
          ))}
        </div>
      ) : (
        <div className="tr-table-560" style={{ border: '1px solid #ebebea', borderRadius: 10, overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '28px 88px minmax(0,1fr) 64px 92px 56px', alignItems: 'center', height: 40, padding: '0 16px', background: '#fafaf9', borderBottom: '1px solid #ebebea', fontSize: 12, fontWeight: 500, color: '#6b6b70', gap: 16 }}>
            <div />
            <div />
            <div>Collection</div>
            <div>Studies</div>
            <div>Visibility</div>
            <div>Saves</div>
          </div>
          {rows.map((c) => {
            const vis = collectionVis(c);
            return (
              <div key={c.public_id} className="tr-hover-soft-fg" onClick={() => nav(`${routeBase()}/collections/${c.public_id}`)} style={{ display: 'grid', gridTemplateColumns: '28px 88px minmax(0,1fr) 64px 92px 56px', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0ee', gap: 16, cursor: 'pointer', background: selected[c.public_id] ? '#fafaf9' : '#fff' }}>
                {editable ? <Checkbox checked={!!selected[c.public_id]} onToggle={() => toggle(c.public_id)} label={`Select ${c.name}`} /> : <div />}
                <MosaicMini c1={c.covers[0] ? studyThumbnail(c.covers[0]) : undefined} c2={c.covers[1] ? studyThumbnail(c.covers[1]) : undefined} c3={c.covers[2] ? studyThumbnail(c.covers[2]) : undefined} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                  <div className="tr-ellipsis" style={{ fontSize: 12.5, color: '#6b6b70' }}>{plainText(c.description)}</div>
                  <div style={{ fontSize: 12, color: '#8a8a8f' }}>{c.author ?? ''}</div>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{c.studies}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b6b70' }}>
                  <VisIcon vis={vis} />
                  {VIS_LABEL[vis]}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    m.like.mutate({ kind: 'collection', id: c.public_id, liked: c.liked });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#6b6b70' }}
                >
                  <IconHeart size={13} fill={c.liked ? '#d9342b' : 'none'} color={c.liked ? '#d9342b' : 'currentColor'} />
                  {c.like_count}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Pager total={total} pageSize={PAGE_SIZE} page={page} noun="collections" onChange={setPage} />

      {editable && selIds.length > 0 ? (
        <SelectionToolbar
          simple
          label={`${selIds.length} ${selIds.length === 1 ? 'collection' : 'collections'} selected`}
          onSelectAll={selectAll}
          allSelected={allSelected}
          copyWorkspaces={copyWorkspaces}
          onCopyTo={copySelected}
          onDelete={deleteSelected}
          onClear={clearSelection}
        />
      ) : null}
    </div>
  );
}

// Unused export kept for the kit page's link copy demo.
export const _copy = (s: string) => copyText(s).then(() => toast('Copied'));
